/** "Validate…" dialog — evaluate a feature against a context and show every
 *  level checked.
 *
 * Context sources reuse the test-render `ContextSourcePicker` (pasted JSON /
 * Event_Delivery_Log id / entity id); there is no event here to infer the
 * entity kind from, so the dialog owns an "Entity type" select (user /
 * tour participant / order). Optional explicit scope overrides (tenant / OTA)
 * cover pasted JSON that carries no entity ids; a notification id supplies the
 * notification-level scope.
 */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as nffApi from '@/api/notificationFeatures';
import type { NotificationFeatureDef, ValidateResult } from '@/api/notificationFeatures';
import { AppDialog } from '@/components/AppDialog';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { useScopedTenantState } from '@/scope/useScopedTenantState';

import { flagRowActions } from './featureFlagLinks';

import {
  ContextSourcePicker,
  EMPTY_CONTEXT_SOURCE,
  contextSourceComplete,
  type ContextSourceValue,
  type EntityKind,
} from '../notifications/test-render/ContextSourcePicker';

const ENTITY_KIND_LABELS: Record<EntityKind, string> = {
  user: 'User',
  gtu: 'Tour participant (booking)',
  order: 'Order',
};

interface Props {
  open: boolean;
  featureDefs: NotificationFeatureDef[];
  onClose: () => void;
}

export function ValidateFeatureDialog({ open, featureDefs, onClose }: Props) {
  const [feature, setFeature] = useState('');
  const [source, setSource] = useState<ContextSourceValue>(EMPTY_CONTEXT_SOURCE);
  const [entityKind, setEntityKind] = useState<EntityKind>('gtu');
  const [tenant, setTenant] = useScopedTenantState();
  const [notificationId, setNotificationId] = useState('');

  const validateMutation = useMutation<ValidateResult, Error>({
    mutationFn: () =>
      nffApi.validateFeature({
        feature,
        ...(notificationId.trim() ? { notification: Number(notificationId) } : {}),
        context:
          source.mode === 'json'
            ? { mode: 'json', json: source.jsonText }
            : source.mode === 'event_delivery_log'
              ? { mode: 'event_delivery_log', event_delivery_log_id: source.edlId.trim() }
              : { mode: 'entity', entity_kind: entityKind, entity_id: Number(source.entityId) },
        ...(tenant ? { scopes: { tour_operator: tenant.id } } : {}),
      }),
  });

  const result = validateMutation.data;
  const error =
    validateMutation.error instanceof ConsoleApiError
      ? validateMutation.error.message
      : validateMutation.error?.message;

  const canRun =
    feature !== '' && contextSourceComplete(source, entityKind) && !validateMutation.isPending;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Validate a feature in context"
      size="md"
      actions={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button variant="contained" disabled={!canRun} onClick={() => validateMutation.mutate()}>
            Evaluate
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
        <TextField
          select
          required
          size="small"
          label="Feature"
          value={feature}
          onChange={(e) => setFeature(e.target.value)}
        >
          {featureDefs.map((f) => (
            <MenuItem key={f.codename} value={f.codename}>
              {f.name}{' '}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: 'monospace', ml: 0.5 }}
              >
                {f.codename}
              </Typography>
            </MenuItem>
          ))}
        </TextField>

        <ContextSourcePicker value={source} onChange={setSource} entityKind={entityKind} />
        {source.mode === 'entity' && (
          <TextField
            select
            size="small"
            label="Entity type"
            value={entityKind}
            onChange={(e) => setEntityKind(e.target.value as EntityKind)}
          >
            {(Object.keys(ENTITY_KIND_LABELS) as EntityKind[]).map((k) => (
              <MenuItem key={k} value={k}>
                {ENTITY_KIND_LABELS[k]}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <TenantPicker
              value={tenant}
              onChange={setTenant}
              label="Tenant scope override (optional)"
            />
          </Box>
          <TextField
            size="small"
            type="number"
            label="Notification ID (optional)"
            value={notificationId}
            onChange={(e) => setNotificationId(e.target.value)}
            sx={{ width: 200 }}
          />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {result && (
          <>
            <Alert severity={result.enabled ? 'success' : 'warning'}>
              Feature “{featureDefs.find((f) => f.codename === result.feature)?.name ??
                result.feature}” is <strong>{result.enabled ? 'ENABLED' : 'DISABLED'}</strong> in
              this context — {result.reason}
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Level</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell align="center">Verdict</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Comment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.checks.map((c) => (
                  <TableRow
                    key={c.level}
                    sx={
                      c.decisive
                        ? { '& td': { fontWeight: 600 }, borderLeft: 3, borderColor: 'error.main' }
                        : undefined
                    }
                  >
                    <TableCell>{c.level}</TableCell>
                    <TableCell>
                      {c.scope?.label || (c.applicable ? '—' : '')}
                      {/* WHICH row decided this. A flag row has no detail page,
                          so the link is the grid's `?q=id=` deep link. */}
                      {c.row_id !== null && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          <ValueActions actions={flagRowActions(c.row_id)}>
                            row #{c.row_id}
                          </ValueActions>
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {!c.applicable ? (
                        <Tooltip title="Not applicable in this context">
                          <RemoveIcon fontSize="small" color="disabled" />
                        </Tooltip>
                      ) : c.enabled ? (
                        <CheckCircleOutlineIcon fontSize="small" color="success" />
                      ) : (
                        <HighlightOffIcon fontSize="small" color="error" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{c.reason}</Typography>
                      {/* Tag-level checks carry a structured per-chip verdict. */}
                      {c.detail && c.detail.chips.length > 0 && (
                        <Typography variant="caption" component="div">
                          {c.detail.chips.map((ch, i) => (
                            <Box
                              key={i}
                              component="span"
                              sx={{ color: ch.matched ? 'success.main' : 'error.main' }}
                            >
                              {i > 0 && <Box component="span" sx={{ color: 'text.disabled' }}>{' · '}</Box>}
                              {ch.matched ? '✓' : '✗'} {ch.label}
                            </Box>
                          ))}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {c.comment}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Box>
    </AppDialog>
  );
}
